import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Button } from '../generic/Core'
import { FormGroup, FormTextArea } from '../form/FormComponents'
import CloseButton from '../generic/CloseButton'

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: rgba(0, 0, 0, 0.45);
`

const Dialog = styled.div`
    position: fixed;
    z-index: 1055;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${({ theme }) => theme.modal.background};
    border: 1px solid ${({ theme }) => theme.modal.border};
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1rem ${({ theme }) => theme.modal.shadow_m};
    width: min(420px, 90vw);
    padding: 0;
    color: ${({ theme }) => theme.form.font};
`

const DialogHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.modal.divider};

    h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
    }
`

const DialogBody = styled.div`
    padding: 1rem;
`

const DialogFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 0.75rem 1rem;
    border-top: 1px solid ${({ theme }) => theme.modal.divider};
    gap: 0.5rem;
`

const UrlInput = styled.input`
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.form.font};
    background: ${({ theme }) => theme.form.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border: ${({ theme }) => theme.form.textControl.activeBorder};
        box-shadow: ${({ theme }) => theme.form.textControl.activeBoxShadow};
    }
`

export default function LinkDialog({ show, onClose, onConfirm, initialUrl = '' }) {
    const [url, setUrl] = useState(initialUrl)
    const inputRef = useRef(null)
    const ignoreNextClick = useRef(false)

    useEffect(() => {
        if (show) {
            setUrl(initialUrl)
            ignoreNextClick.current = true
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }, [show, initialUrl])

    useEffect(() => {
        if (!show) return

        const handleClick = (e) => {
            if (ignoreNextClick.current) {
                ignoreNextClick.current = false
                return
            }
            onClose()
        }

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose()
        }

        document.addEventListener('click', handleClick)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('click', handleClick)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [show, onClose])

    if (!show) return null

    const handleConfirm = () => {
        const trimmed = url.trim()
        if (!trimmed) return
        onConfirm(trimmed)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleConfirm() }
    }

    return createPortal(
        <>
            <Backdrop onClick={onClose} />
            <Dialog onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <h4>Insert Link</h4>
                    <CloseButton handleClick={onClose} $size="1.5rem" />
                </DialogHeader>
                <DialogBody>
                    <FormGroup>
                        <FormGroup.Label>URL</FormGroup.Label>
                        <UrlInput
                            ref={inputRef}
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="https://example.com"
                            autoComplete="off"
                        />
                    </FormGroup>
                </DialogBody>
                <DialogFooter>
                    <Button $variant="secondary" type="button" onClick={onClose}>Cancel</Button>
                    <Button $variant="primary" type="button" onClick={handleConfirm} disabled={!url.trim()}>Insert</Button>
                </DialogFooter>
            </Dialog>
        </>,
        document.body
    )
}
